import { claveTexto, STOPWORDS_LUGAR } from '../pg/normalizar';
import {
  ESTADO_MERCADO, LINEAS_KELLOGGS, MARCAS_MERCADO, T4B, T4B_OPS, ladoDeMarca,
} from './catalogo';
import {
  brecha, contar, contarEnOrden, mediana, resumirBrechas, type Conteo, type ResumenBrechas,
} from './comun';
import { etiquetaOpcion } from './respuestas';
import {
  arr, num, obj, str, strs, NOMBRE_MARCA,
  type CierreYiMercado, type FichaYiBodega, type FichaYiMercado, type Json, type MarcaCliente, type Presencia,
} from './tipos';

/**
 * La aritmética del canal tradicional de Yichang: bodegas y mercados.
 *
 * Sale de la ficha que arman `armaFichaBod` y `enviarMer` en `app-tradicional/index.html`. Dos
 * cosas de esa ficha que cambian cómo se cuenta:
 *
 * - **Las ausencias se guardan con su estado, nunca en blanco.** T4b siempre trae valor: si el
 *   producto se vio en T4 dice `se_vio_en_t4`, y si la bodega no vende la categoría dice
 *   `no_vende_la_categoria`. Un T4b vacío es de verdad «sin respuesta».
 * - **El precio por 100 g ya viene calculado** (`precio_x100g`), con la fórmula de la pantalla. Se usa
 *   ese y, solo si falta, se recalcula con la misma fórmula.
 */

export const SIN_RESPUESTA = 'Sin respuesta';

/**
 * Las opciones de T6 y m8.1 que no son una respuesta: están en la lista del formulario, pero contarlas
 * como «contestó» inflaría el denominador y bajaría el porcentaje de cada marca. Se cuentan aparte.
 */
const NO_RESPONDIO: readonly string[] = ['No contestó', 'No se pudo preguntar'];

/** Una pregunta referencial repartida en las que respondieron y las que no. */
function respuestasReferenciales(vs: readonly string[]): { contestaron: number; marcas: Conteo[]; noRespondio: number } {
  const si = vs.filter((v) => !NO_RESPONDIO.includes(v));
  return { contestaron: si.length, marcas: contar(si), noRespondio: vs.length - si.length };
}

// ═══════════════════════════════════════════════════════════════════════════
// Precios
// ═══════════════════════════════════════════════════════════════════════════

const OTRO = 'Otro · lo escribo';
const GRANEL: readonly string[] = ['A granel, sin marca', 'A granel, por kilo'];

export const esGranel = (p: Json): boolean =>
  GRANEL.includes(str(p.producto) ?? '') || GRANEL.includes(str(p.presentacion) ?? '');

/** Los gramos del empaque, como `gramosDe` del formulario: un rango «18-20 g» vale su punto medio. */
export function gramosTrad(p: Json): number | null {
  if (esGranel(p)) return null;
  if (str(p.gramaje) === OTRO) return num(p.gramaje_otro_g);
  const m = /(\d+(?:[.,]\d+)?)(?:\s*-\s*(\d+(?:[.,]\d+)?))?\s*g/.exec(str(p.gramaje) ?? '');
  if (!m) return null;
  const a = parseFloat(m[1].replace(',', '.'));
  const b = m[2] !== undefined ? parseFloat(m[2].replace(',', '.')) : null;
  return b !== null ? (a + b) / 2 : a;
}

/**
 * El precio regular por 100 g. **No se pregunta: lo calcula la pantalla** y lo deja escrito en la
 * ficha. A granel el precio ya es por kilo y no hay gramaje: ahí da `null`, no un número inventado.
 */
export function por100Trad(p: Json | null): number | null {
  if (p === null) return null;
  const guardado = num(p.precio_x100g);
  if (guardado !== null) return guardado;
  const v = num(p.precio_regular);
  if (v === null || esGranel(p)) return null;
  const g = gramosTrad(p);
  if (g === null || g <= 0) return null;
  const u = num(p.unidades);
  return (v / (g * (u !== null && u > 0 ? u : 1))) * 100;
}

/** Cómo se llama un producto de precio: la marca elegida o, si fue «Otro», lo que se escribió. */
export const productoDe = (p: Json): string | null =>
  (['Otro · lo escribo', 'Otra'].includes(str(p.producto) ?? '') ? (str(p.producto_otro) ?? str(p.producto)) : str(p.producto));

// ═══════════════════════════════════════════════════════════════════════════
// Bodegas
// ═══════════════════════════════════════════════════════════════════════════

const T4B_NO: readonly string[] = ['se_le_acabo', 'no_la_trabaja', 'no_vende_la_categoria'];
const T4B_SI: readonly string[] = ['la_saco', 'se_vio_en_t4'];
const CON_PRODUCTO: readonly string[] = ['lleno_su_marca', 'a_medias_su_marca'];
const FACHADA: readonly string[] = [
  't2_2_letrero', 't2_3_pintada', 't2_4_toldo', 't2_5_reja', 't2_6_viniles', 't2_7_afiches', 't2_8_jalavistas',
];

const t4bDe = (marca: MarcaCliente) => T4B.filter((x) => x.marca === marca);
const claveT0 = (marca: MarcaCliente) => (marca === 'pringles' ? 't0_1_vende_snacks' : 't0_2_vende_cereal');

/** Lo que se vio de la marca en T4 (T4.1 · T4.4), con el nombre corto. */
export function vistosBodega(f: FichaYiBodega, marca: MarcaCliente): string[] {
  if (marca === 'pringles') {
    return strs(f.ficha.t4_1_snacks_vistos).filter((s) => /^Pringles/.test(s))
      .map((s) => s.replace(/^Pringles · /, ''));
  }
  return strs(f.ficha.t4_4_cereales_vistos).filter((s) => LINEAS_KELLOGGS.includes(s))
    .map((s) => (s === LINEAS_KELLOGGS[4] ? "otra línea de Kellogg's" : s));
}

/**
 * ¿Hay prueba de la marca en la bodega? Se vio a la vista (T4.1 · T4.4), el bodeguero la sacó de
 * atrás (T4b «Sí, la sacó»), se dice dónde está Pringles (T3.7), o un mueble de la marca tenía su
 * producto (T3.3).
 *
 * ⚠️ «Dice que tiene, pero no la sacó» NO es prueba: es declarado, y así lo rotula el formulario.
 */
function hayEvidenciaBodega(f: FichaYiBodega, marca: MarcaCliente): boolean {
  if (vistosBodega(f, marca).length > 0) return true;
  if (t4bDe(marca).some((x) => T4B_SI.includes(str(f.ficha[x.k]) ?? ''))) return true;
  if (marca === 'pringles'
    && ['exhibidor_pringles', 'exhibidor_otra_marca', 'sin_exhibidor'].includes(str(f.ficha.t3_7_donde_esta_pringles) ?? '')) {
    return true;
  }
  return arr(f.ficha.t3_elementos).map(obj).some((e) => e !== null
    && str(e.t3_2_marca) === NOMBRE_MARCA[marca] && CON_PRODUCTO.includes(str(e.t3_3_contenido) ?? ''));
}

/**
 * ¿Se encontró la marca en la bodega?
 *
 * - **presente**: hay prueba (ver arriba).
 * - **ausente**: la bodega no vende la categoría (T0), o **todos** los productos núcleo de la marca
 *   que se preguntan en T4b quedaron en «se le acabó», «no la trabaja» o «no vende la categoría».
 *   «Se le acabó» es ausencia HOY —es un quiebre—, aunque la bodega la trabaje.
 * - **no se sabe**: cualquier otra cosa: «no contestó», «no se pudo preguntar», «dice que tiene»,
 *   un T4b en blanco. No se cuenta como ausente.
 */
export function presenciaBodega(f: FichaYiBodega, marca: MarcaCliente): Presencia {
  if (hayEvidenciaBodega(f, marca)) return 'presente';
  if (str(f.ficha[claveT0(marca)]) === 'no_vende') return 'ausente';
  const t4b = t4bDe(marca).map((x) => str(f.ficha[x.k]));
  if (t4b.length > 0 && t4b.every((v) => T4B_NO.includes(v ?? ''))) return 'ausente';
  return 'no_se_sabe';
}

export interface ChoqueTrad {
  estado: 'no_aplica' | 'sin_par' | 'falta_lado' | 'falta_precio' | 'completo';
  par: string | null;
  /** Por qué falta cada lado (T5.7), si falta. */
  faltaA: string | null;
  faltaB: string | null;
  a100: number | null;
  b100: number | null;
  brechaPct: number | null;
}

/**
 * El choque obligatorio de la marca (T5.6): Pringles chica contra Lay's o Piqueo Snax; la bolsa de
 * Kellogg's contra la de Ángel. La ficha no lo trae cuando la bodega no vende la categoría.
 */
export function choqueBodega(f: FichaYiBodega, marca: MarcaCliente): ChoqueTrad {
  const base: ChoqueTrad = {
    estado: 'no_aplica', par: null, faltaA: null, faltaB: null, a100: null, b100: null, brechaPct: null,
  };
  const ch = obj(f.ficha[marca === 'pringles' ? 't5_choque_pringles' : 't5_choque_kelloggs']);
  if (ch === null) return base;
  const par = str(ch.par);
  if (par === null) return { ...base, estado: 'sin_par' };
  const a = obj(ch.lado_a);
  const b = obj(ch.lado_b);
  const faltaA = str(a?.falta);
  const faltaB = str(b?.falta);
  if (faltaA !== null || faltaB !== null) return { ...base, estado: 'falta_lado', par, faltaA, faltaB };
  const a100 = por100Trad(a);
  const b100 = por100Trad(b);
  if (a100 === null || b100 === null || b100 <= 0) return { ...base, estado: 'falta_precio', par, a100, b100 };
  return { ...base, estado: 'completo', par, a100, b100, brechaPct: brecha(a100, b100) };
}

/** Una línea por marca para la columna «Qué se midió». */
export function queSeMidioBodega(f: FichaYiBodega, marca: MarcaCliente): string {
  const t0 = str(f.ficha[claveT0(marca)]);
  const partes = [`vende ${marca === 'pringles' ? 'snacks' : 'cereal'}: ${t0 === null ? 'sin respuesta' : etiquetaOpcion(t0, claveT0(marca)).toLowerCase()}`];
  if (t0 === 'no_vende') return partes.join(' · ');
  const vistos = vistosBodega(f, marca);
  if (vistos.length > 0) partes.push(`a la vista: ${vistos.join(', ')}`);
  for (const x of t4bDe(marca)) {
    const v = str(f.ficha[x.k]);
    if (v !== null && v !== 'se_vio_en_t4' && v !== 'no_vende_la_categoria') {
      partes.push(`${x.nom}: ${etiquetaOpcion(v, x.k).toLowerCase()}`);
    }
  }
  const ch = choqueBodega(f, marca);
  if (ch.estado === 'completo' && ch.brechaPct !== null) {
    partes.push(`choque ${ch.brechaPct > 0 ? '+' : ''}${ch.brechaPct} % por 100 g`);
  } else if (ch.estado !== 'no_aplica') partes.push('choque sin calcular');
  return partes.join(' · ');
}

export interface ResumenBodegas {
  n: number;
  /** T0 de la categoría de la marca, sobre todas las bodegas. */
  vende: Conteo[];
  presencia: Record<Presencia, number>;
  /** T4b producto por producto, sobre todas las bodegas, en el orden de las respuestas. */
  t4b: { k: string; cod: string; nom: string; conteo: Conteo[] }[];
  /** T4c: sobre las bodegas que contestaron por qué no la trabajan. */
  t4c: { contestaron: number; motivos: Conteo[] };
  /** T7: sobre las bodegas que contestaron dónde la compran. */
  t7: { contestaron: number; donde: Conteo[]; frecuencia: Conteo[]; pago: Conteo[] };
  choque: {
    aplica: number;
    sinPar: number;
    faltaLado: Conteo[];
    faltaPrecio: number;
    pares: Conteo[];
    brechas: ResumenBrechas;
  };
  /** T6.2 · T6.3, referencial: sobre los que contestaron. */
  recomienda: { contestaron: number; marcas: Conteo[]; noRespondio: number };
  /** T3.7, solo Pringles, sobre las que venden snacks y contestaron. */
  dondePringles: Conteo[] | null;
  /** Bodegas con algún mueble de la marca (T3.2) y con material de la marca afuera (T2.2 a T2.8). */
  conMueble: number;
  conFachada: number;
}

export function resumenBodegas(fichas: readonly FichaYiBodega[], marca: MarcaCliente): ResumenBodegas {
  const nombre = NOMBRE_MARCA[marca];
  const k0 = claveT0(marca);
  const presencia: Record<Presencia, number> = { presente: 0, ausente: 0, no_se_sabe: 0 };
  for (const f of fichas) presencia[presenciaBodega(f, marca)] += 1;

  const ordenT4b = [...T4B_OPS.map(([, l]) => l), SIN_RESPUESTA];
  const t4b = t4bDe(marca).map((x) => ({
    k: x.k, cod: x.cod, nom: x.nom,
    conteo: contarEnOrden(fichas.map((f) => {
      const v = str(f.ficha[x.k]);
      return v === null ? SIN_RESPUESTA : etiquetaOpcion(v, x.k);
    }), ordenT4b),
  }));

  const t4cClave = marca === 'pringles' ? 't4c_1_por_que_no_pringles' : 't4c_2_por_que_no_kelloggs';
  const t4cOtro = marca === 'pringles' ? 't4c_1_otro' : 't4c_2_otro';
  const conT4c = fichas.filter((f) => strs(f.ficha[t4cClave]).length > 0);

  const conT7 = fichas.filter((f) => strs(f.ficha[`t7_1_donde_compra_${marca}`]).length > 0);

  const choques = fichas.map((f) => choqueBodega(f, marca));
  const recKey = marca === 'pringles' ? 't6_2_recomienda_snacks' : 't6_3_recomienda_cereal';
  const recOtra = marca === 'pringles' ? 't6_2_otra' : 't6_3_otra';
  const rec = fichas.map((f) => {
    const v = str(f.ficha[recKey]);
    return v === 'Otra' ? (str(f.ficha[recOtra]) ?? 'Otra') : v;
  }).filter((v): v is string => v !== null);

  const tieneMarca = (v: unknown): boolean => (Array.isArray(v) ? strs(v).includes(nombre) : str(v) === nombre);

  return {
    n: fichas.length,
    vende: contar(fichas.map((f) => {
      const v = str(f.ficha[k0]);
      return v === null ? SIN_RESPUESTA : etiquetaOpcion(v, k0);
    })),
    presencia,
    t4b,
    t4c: {
      contestaron: conT4c.length,
      motivos: contar(conT4c.flatMap((f) => strs(f.ficha[t4cClave])
        .map((m) => (m === OTRO ? (str(f.ficha[t4cOtro]) ?? m) : m)))),
    },
    t7: {
      contestaron: conT7.length,
      donde: contar(conT7.flatMap((f) => strs(f.ficha[`t7_1_donde_compra_${marca}`]))),
      frecuencia: contar(conT7.map((f) => str(f.ficha[`t7_2_frecuencia_${marca}`])).filter((v): v is string => v !== null)),
      pago: contar(conT7.map((f) => str(f.ficha[`t7_3_pago_${marca}`])).filter((v): v is string => v !== null)),
    },
    choque: {
      aplica: choques.filter((c) => c.estado !== 'no_aplica').length,
      sinPar: choques.filter((c) => c.estado === 'sin_par').length,
      faltaLado: contar(choques.filter((c) => c.estado === 'falta_lado')
        .flatMap((c) => [c.faltaA, c.faltaB]).filter((v): v is string => v !== null)
        .map((v) => etiquetaOpcion(v))),
      faltaPrecio: choques.filter((c) => c.estado === 'falta_precio').length,
      pares: contar(choques.map((c) => c.par).filter((v): v is string => v !== null).map((v) => etiquetaOpcion(v))),
      brechas: resumirBrechas(choques.map((c) => c.brechaPct).filter((b): b is number => b !== null)),
    },
    recomienda: respuestasReferenciales(rec),
    dondePringles: marca === 'pringles'
      ? contar(fichas.map((f) => str(f.ficha.t3_7_donde_esta_pringles))
        .filter((v): v is string => v !== null && v !== 'no_vende_la_categoria')
        .map((v) => etiquetaOpcion(v, 't3_7_donde_esta_pringles')))
      : null,
    conMueble: fichas.filter((f) => arr(f.ficha.t3_elementos).map(obj)
      .some((e) => e !== null && str(e.t3_2_marca) === nombre)).length,
    conFachada: fichas.filter((f) => FACHADA.some((k) => tieneMarca(f.ficha[k]))).length,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Mercados
// ═══════════════════════════════════════════════════════════════════════════

const catDeMarca = (marca: MarcaCliente): 'snacks' | 'cereales' => (marca === 'pringles' ? 'snacks' : 'cereales');
const lineasDe = (marca: MarcaCliente): readonly string[] => (marca === 'pringles' ? ['Pringles'] : LINEAS_KELLOGGS);
const ESTADO_NO: readonly string[] = ['se_le_acabo', 'no_la_trabaja'];

/** Las categorías que vende el puesto (m2.2), sin «Nada de esto». Vacío = no contestó o nada. */
export const categoriasMer = (f: FichaYiMercado): string[] => strs(f.ficha.m2_2_categorias);

/**
 * ¿Se encontró la marca en el puesto?
 *
 * - **presente**: m4.1 dice «Tiene» (Pringles, o cualquiera de las líneas de Kellogg's).
 * - **ausente**: el puesto contestó qué categorías vende y no vende la de la marca; o m4.1 dice «se
 *   le acabó» o «no la trabaja» para **todas** las líneas de la marca.
 * - **no se sabe**: el puesto no se pudo levantar, no contestó m2.2, o m4.1 quedó en «no se pudo
 *   saber» o en blanco. Un puesto cerrado no es un puesto sin Pringles.
 */
export function presenciaMercado(f: FichaYiMercado, marca: MarcaCliente): Presencia {
  if (!f.levantado) return 'no_se_sabe';
  const e = obj(f.ficha.m4_1_estado_por_marca) ?? {};
  const lineas = lineasDe(marca);
  if (lineas.some((l) => str(e[l]) === 'tiene')) return 'presente';
  const cats = categoriasMer(f);
  if (cats.length > 0 && !cats.includes(catDeMarca(marca))) return 'ausente';
  if (lineas.every((l) => ESTADO_NO.includes(str(e[l]) ?? ''))) return 'ausente';
  return 'no_se_sabe';
}

/** Los precios de m5 que son de la categoría de la marca. */
export const preciosMerDe = (f: FichaYiMercado, marca: MarcaCliente): Json[] =>
  arr(f.ficha.m5_precios).map(obj)
    .filter((p): p is Json => p !== null && str(p.m5_1_categoria) === catDeMarca(marca));

export function queSeMidioMercado(f: FichaYiMercado, marca: MarcaCliente): string {
  if (!f.levantado) return 'no se pudo levantar';
  const cats = categoriasMer(f);
  if (cats.length === 0) return 'sin categorías contestadas';
  if (!cats.includes(catDeMarca(marca))) return `no vende ${marca === 'pringles' ? 'snacks' : 'cereales'}`;
  const e = obj(f.ficha.m4_1_estado_por_marca) ?? {};
  const partes: string[] = [];
  if (marca === 'pringles') {
    const v = str(e.Pringles);
    partes.push(`Pringles: ${v === null ? 'sin respuesta' : etiquetaOpcion(v, 'm4_1_estado_por_marca').toLowerCase()}`);
    const tam = strs(f.ficha.m4_2_pringles_tamanos);
    if (tam.length > 0) partes.push(tam.join(', ').toLowerCase());
  } else {
    const tiene = LINEAS_KELLOGGS.filter((l) => str(e[l]) === 'tiene');
    partes.push(`${tiene.length} de ${LINEAS_KELLOGGS.length} líneas de Kellogg's «Tiene»`);
  }
  const propios = preciosMerDe(f, marca).filter((p) => lineasDe(marca).includes(str(p.producto) ?? ''));
  partes.push(`${propios.length} ${propios.length === 1 ? 'precio' : 'precios'} de ${NOMBRE_MARCA[marca]}`);
  return partes.join(' · ');
}

/** Lo del recorrido que NO depende de la marca: se calcula una sola vez, sin elegir una. */
export interface ResumenPuestos {
  visitados: number;
  levantados: number;
  noSePudo: number;
  motivos: Conteo[];
  /** m7: es del puesto, no de una marca. */
  abasto: { contestaron: number; donde: Conteo[]; frecuencia: Conteo[] };
  jornadas: { mercadoDias: number; cerradas: number };
}

export interface ResumenMercados extends ResumenPuestos {
  /** Puestos levantados que venden la categoría de la marca: el denominador de m4. */
  vendenCategoria: number;
  presencia: Record<Presencia, number>;
  /** m4.1, marca por marca de la categoría, sobre los puestos que la venden. */
  estados: { marca: string; esCliente: boolean; conteo: Conteo[] }[];
  /** m4.2, solo Pringles, sobre los puestos con Pringles «Tiene». Se puede marcar más de uno. */
  tamanos: { de: number; conteo: Conteo[] } | null;
  /** m4.3, solo Kellogg's, sobre los puestos que venden cereales o avena y contestaron. */
  granel: Conteo[] | null;
  /** m5, por marca de la categoría: cuántos precios y la mediana por 100 g donde se pudo calcular. */
  precios: { marca: string; esCliente: boolean; n: number; con100: number; mediana100: number | null }[];
  /** m8.1, referencial. */
  ofrece: { contestaron: number; conteo: Conteo[]; noRespondio: number };
}

const claveMercado = (nombre: string | null): string => claveTexto(nombre, STOPWORDS_LUGAR) ?? '—';

/**
 * Los puestos del recorrido, sin mirar ninguna marca: cuántos se visitaron y levantaron, por qué no
 * se pudo, de dónde se abastecen (m7) y qué jornadas se cerraron.
 */
export function resumenPuestos(fichas: readonly FichaYiMercado[], cierres: readonly CierreYiMercado[]): ResumenPuestos {
  const levantados = fichas.filter((f) => f.levantado);
  const conAbasto = levantados.filter((f) => strs(f.ficha.m7_1_donde_saca).length > 0);
  const dias = new Set(fichas.map((f) => `${claveMercado(f.mercadoNombre)}|${f.fecha}`));
  const cerrados = new Set(cierres.map((c) => `${claveMercado(c.mercadoNombre)}|${c.fecha}`));
  return {
    visitados: fichas.length,
    levantados: levantados.length,
    noSePudo: fichas.length - levantados.length,
    motivos: contar(fichas.filter((f) => !f.levantado).map((f) => f.motivoNo ?? SIN_RESPUESTA)),
    abasto: {
      contestaron: conAbasto.length,
      donde: contar(conAbasto.flatMap((f) => strs(f.ficha.m7_1_donde_saca))),
      frecuencia: contar(conAbasto.map((f) => str(f.ficha.m7_2_frecuencia)).filter((v): v is string => v !== null)),
    },
    jornadas: { mercadoDias: dias.size, cerradas: [...dias].filter((d) => cerrados.has(d)).length },
  };
}

export function resumenMercados(
  fichas: readonly FichaYiMercado[], cierres: readonly CierreYiMercado[], marca: MarcaCliente,
): ResumenMercados {
  const levantados = fichas.filter((f) => f.levantado);
  const cat = catDeMarca(marca);
  const venden = levantados.filter((f) => categoriasMer(f).includes(cat));
  const presencia: Record<Presencia, number> = { presente: 0, ausente: 0, no_se_sabe: 0 };
  for (const f of fichas) presencia[presenciaMercado(f, marca)] += 1;

  const ordenEstado = [...ESTADO_MERCADO.map(([, l]) => l), SIN_RESPUESTA];
  const clientes = lineasDe(marca);
  const estados = MARCAS_MERCADO[cat].map((m) => ({
    marca: m,
    esCliente: clientes.includes(m),
    conteo: contarEnOrden(venden.map((f) => {
      const v = str(obj(f.ficha.m4_1_estado_por_marca)?.[m]);
      return v === null ? SIN_RESPUESTA : etiquetaOpcion(v, 'm4_1_estado_por_marca');
    }), ordenEstado),
  }));

  const conPringles = venden.filter((f) => str(obj(f.ficha.m4_1_estado_por_marca)?.Pringles) === 'tiene');

  const precios = venden.flatMap((f) => preciosMerDe(f, marca));
  const porMarca = [...new Set(precios.map((p) => productoDe(p) ?? SIN_RESPUESTA))];
  const filasPrecio = porMarca.map((m) => {
    const ps = precios.filter((p) => (productoDe(p) ?? SIN_RESPUESTA) === m);
    const x100 = ps.map(por100Trad).filter((v): v is number => v !== null);
    return { marca: m, esCliente: clientes.includes(m), n: ps.length, con100: x100.length, mediana100: mediana(x100) };
  }).sort((a, b) => Number(b.esCliente) - Number(a.esCliente) || b.n - a.n || a.marca.localeCompare(b.marca, 'es'));

  const ofreceKey = marca === 'pringles' ? 'm8_1_ofrece_snacks' : 'm8_1_ofrece_cereales';
  const ofreceOtra = marca === 'pringles' ? 'm8_1_otra_snacks' : 'm8_1_otra_cereales';
  const ofrece = venden.map((f) => {
    const v = str(f.ficha[ofreceKey]);
    return v === 'Otra' ? (str(f.ficha[ofreceOtra]) ?? 'Otra') : v;
  }).filter((v): v is string => v !== null);
  const ofreceR = respuestasReferenciales(ofrece);

  return {
    ...resumenPuestos(fichas, cierres),
    vendenCategoria: venden.length,
    presencia,
    estados,
    tamanos: marca === 'pringles'
      ? { de: conPringles.length, conteo: contar(conPringles.flatMap((f) => strs(f.ficha.m4_2_pringles_tamanos))) }
      : null,
    granel: marca === 'kelloggs'
      ? contar(levantados.map((f) => str(f.ficha.m4_3_cereal_a_granel)).filter((v): v is string => v !== null)
        .map((v) => etiquetaOpcion(v)))
      : null,
    precios: filasPrecio,
    ofrece: { contestaron: ofreceR.contestaron, conteo: ofreceR.marcas, noRespondio: ofreceR.noRespondio },
  };
}

/** El lado de una promoción del mercado (m6) o de la bodega (T5.10): por su marca. */
export function ladoDePromo(p: Json): MarcaCliente | null {
  const m = str(p.m6_2_marca) ?? str(p.t5_10_marca);
  if (m === 'Otra') return ladoDeMarca(str(p.m6_2_marca_otra) ?? str(p.t5_10_marca_otra));
  return ladoDeMarca(m);
}

/** El lado de un precio suelto del tradicional: por su categoría en mercados, por su producto en bodegas. */
export function ladoDePrecio(p: Json): MarcaCliente | null {
  const c = str(p.m5_1_categoria);
  if (c === 'snacks') return 'pringles';
  if (c === 'cereales') return 'kelloggs';
  return ladoDeMarca(productoDe(p));
}
